/** The map layer: Leaflet with the prototype's four map types, marker look and route colours. */
import L from 'leaflet';
import { leafletLayer } from 'protomaps-leaflet';
import type { PMTiles } from 'pmtiles';
import type { LatLng } from '@konkride/shared';

export type MapType = 'road' | 'clean' | 'terrain' | 'sat';
export const MT: Array<[MapType, string, string]> = [['road', 'Roads', 'Standard street map'], ['clean', 'Clean roads', 'Road network only, light'], ['terrain', 'Terrain', 'Hills and contours'], ['sat', 'Satellite', 'Aerial photos']];
const MTU: Record<MapType, [string, number, string]> = {
  road: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png', 19, '&copy; OpenStreetMap'],
  clean: ['https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', 19, '&copy; OpenStreetMap, &copy; CARTO'],
  terrain: ['https://tile.opentopomap.org/{z}/{x}/{y}.png', 17, '&copy; OpenTopoMap, OpenStreetMap'],
  sat: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', 18, 'Imagery &copy; Esri'],
};

export interface Pin { id: string; pos: LatLng; col: string; far?: boolean; dia?: boolean; me?: boolean; drag?: boolean; size?: number }
export interface Line { id: string; pts: Array<[number, number]>; col: string; real: boolean }
interface MapCbs { onTap: (p: LatLng) => void; onDrag: (p: LatLng) => void }

export class MapView {
  private map: L.Map;
  private tiles: L.Layer | null = null;
  private vector: PMTiles | null = null;
  private pins = new Map<string, { m: L.Marker; sig: string }>();
  private lines = new Map<string, { l: L.Polyline; sig: string }>();
  private type: MapType = 'road';
  private road: [string, string] | null = null;
  constructor(private el: HTMLElement, private cb: MapCbs, start: LatLng, roadTiles?: { tiles: string; attribution: string }) {
    if (roadTiles) this.road = [roadTiles.tiles, roadTiles.attribution];
    this.map = L.map(el, { center: [start.lat, start.lng], zoom: 14, zoomControl: true, attributionControl: false });
    this.map.on('click', (e: L.LeafletMouseEvent) => this.cb.onTap({ lat: e.latlng.lat, lng: e.latlng.lng }));
    this.setType('road');
  }
  /** Use the city's stored vector tiles for the Roads and Clean roads maps (once they are on the phone). */
  setVector(pm: PMTiles | null): void {
    this.vector = pm;
    this.setType(this.type);
  }
  setType(t: MapType): void {
    this.type = t;
    this.tiles?.remove();
    if (this.vector && (t === 'road' || t === 'clean')) {
      // maxDataZoom 14 is the highest zoom stored; closer zooms are drawn from it, sharp because the data is vectors
      const layer = leafletLayer({ url: this.vector as never, flavor: t === 'clean' ? 'white' : 'light', maxZoom: 19, maxDataZoom: 14, attribution: '' });
      this.tiles = layer.addTo(this.map);
    } else {
      const u = MTU[t] ?? MTU.road;
      const [url, attr] = t === 'road' && this.road ? this.road : [u[0], u[2]];
      this.tiles = L.tileLayer(url, { maxZoom: u[1], maxNativeZoom: u[1], attribution: attr, subdomains: t === 'clean' ? 'abcd' : 'abc' }).addTo(this.map);
    }
    (this.tiles as L.GridLayer).bringToBack();
  }
  get current(): MapType { return this.type; }
  invalidate(): void { this.map.invalidateSize(); }
  /** Put a place near the top of the visible map, above the panel. */
  center(p: LatLng, panelMin: boolean): void {
    this.map.setView([p.lat, p.lng], Math.max(this.map.getZoom(), 14), { animate: false });
    this.map.panBy([0, panelMin ? 28 : Math.round((this.el.clientHeight || 700) * 0.22)], { animate: false });
  }
  pan(p: LatLng): void { this.map.panTo([p.lat, p.lng], { animate: true }); }
  update(pins: Pin[], lines: Line[]): void {
    const want = new Set<string>();
    for (const p of pins) {
      want.add(p.id);
      const sig = [p.col, p.far ? 1 : 0, p.dia ? 1 : 0, p.me ? 1 : 0, p.drag ? 1 : 0].join('|');
      const cur = this.pins.get(p.id);
      if (cur && cur.sig === sig) { if (!(cur.m as L.Marker & { busy?: boolean }).busy) cur.m.setLatLng([p.pos.lat, p.pos.lng]); continue; }
      cur?.m.remove();
      const z = p.drag ? 48 : 26;
      const m = L.marker([p.pos.lat, p.pos.lng], {
        interactive: !!p.drag, draggable: !!p.drag, keyboard: false,
        icon: L.divIcon({ className: '', iconSize: [z, z], iconAnchor: [z / 2, z / 2], html: `<div style="width:${z}px;height:${z}px;display:grid;place-items:center"><div class="osmk ${p.dia ? 'dia' : ''}${p.me ? ' me' : ''}" style="background:${p.col}${p.far ? ';opacity:.4' : ''}"></div></div>` }),
      }).addTo(this.map);
      if (p.drag) {
        m.on('dragstart', () => { (m as L.Marker & { busy?: boolean }).busy = true; });
        m.on('dragend', () => { (m as L.Marker & { busy?: boolean }).busy = false; const l = m.getLatLng(); this.cb.onDrag({ lat: l.lat, lng: l.lng }); });
      }
      this.pins.set(p.id, { m, sig });
    }
    for (const [k, v] of this.pins) if (!want.has(k)) { v.m.remove(); this.pins.delete(k); }
    const wl = new Set<string>();
    for (const ln of lines) {
      wl.add(ln.id);
      const sig = ln.pts.length + '|' + ln.col + '|' + ln.real + '|' + (ln.pts[0] ?? []).join(',') + '|' + (ln.pts[ln.pts.length - 1] ?? []).join(',');
      const cur = this.lines.get(ln.id);
      if (cur && cur.sig === sig) continue;
      cur?.l.remove();
      const l = L.polyline(ln.pts, { color: ln.col, weight: 6, opacity: ln.real ? 0.95 : 0.55, lineCap: 'round', lineJoin: 'round', interactive: false }).addTo(this.map);
      this.lines.set(ln.id, { l, sig });
    }
    for (const [k, v] of this.lines) if (!wl.has(k)) { v.l.remove(); this.lines.delete(k); }
  }
  destroy(): void { this.map.remove(); }
}
